import './Bar.css';
import { type Party, partyClass } from '../theme/parties';

export interface BarSegment {
  /** Fração de 0 a 1. */
  value: number;
  /** Sem partido: herda --p-fill do dono, senão --color-fg-3. */
  party?: Party;
}

export interface BarProps {
  segments: readonly BarSegment[];
  /** Resto (1 − soma): transparente (trilho) ou translúcido a 10% / 8% (§3.8). */
  rest?: 'none' | 'faint' | 'faint-8';
  /** Marca de 50%: cheia (2 px com anel) ou `mini` (1 px). */
  fifty?: boolean | 'mini';
  /** Variante de uso: `line` = linha de candidato (3 px); `mini` = minibarra de região (64×3, some na TV). */
  variant?: 'default' | 'line' | 'mini';
  /** Nome acessível do dono; sem ele a barra é decorativa (os números estão ao lado). */
  label?: string;
}

/** Fração finita entre 0 e 1, com 6 casas (valor de --w). */
function frac(v: number): number {
  if (!Number.isFinite(v) || v <= 0) return 0;
  return Math.round(Math.min(v, 1) * 1e6) / 1e6;
}

/** Barra segmentada (§3.8 `Bar`): fatias em flex-grow sobre o trilho, separador de 1,5 px e marca de 50% opcional. */
export function Bar({ segments, rest = 'none', fifty = false, variant = 'default', label }: BarProps) {
  const values = segments.map((s) => frac(s.value));
  const sum = values.reduce((a, b) => a + b, 0);
  const restValue = rest === 'none' ? 0 : frac(1 - sum);
  const classes = ['bar'];
  if (variant !== 'default') classes.push(`bar--${variant}`);
  if (variant === 'mini') classes.push('tv-hide');
  const a11y = label ? ({ role: 'img', 'aria-label': label } as const) : ({ 'aria-hidden': 'true' } as const);
  return (
    <span
      class={classes.join(' ')}
      data-testid="bar"
      data-state={sum > 0 ? 'filled' : 'empty'}
      data-dataviz=""
      {...a11y}
    >
      <span class="bar__clip">
        {segments.map((s, i) => (
          <i
            key={i}
            class={s.party ? `bar__seg ${partyClass(s.party)}` : 'bar__seg'}
            style={{ '--w': String(values[i]) }}
          />
        ))}
        {restValue > 0 && <span class={`bar__rest bar__rest--${rest}`} style={{ '--w': String(restValue) }} />}
      </span>
      {fifty && <span class={fifty === 'mini' ? 'bar__fifty bar__fifty--mini' : 'bar__fifty'} />}
    </span>
  );
}
